import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react'
import { useMediaQuery } from '@/hooks/use-media-query'
import type { SnapshotComparison as SnapshotComparisonType } from '@/types/attendance-metrics'

interface SnapshotComparisonProps {
  isOpen: boolean
  onClose: () => void
  comparison: SnapshotComparisonType | null
}

function formatTime(seconds: number | null): string {
  if (seconds === null) return 'N/A'
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  return `${(seconds / 60).toFixed(1)}min`
}

function formatDelta(delta: number, lowerIsBetter: boolean = false): {
  text: string
  isPositive: boolean
  icon: React.ReactNode
} {
  const sign = delta >= 0 ? '+' : ''
  const text = `${sign}${delta.toFixed(1)}%`

  // For FRT/TMR, lower is better (negative delta is good)
  // For counts, higher is better (positive delta is good)
  const isPositive = lowerIsBetter ? delta < 0 : delta > 0

  const icon = isPositive ? (
    <TrendingUp className="h-4 w-4 text-green-600" />
  ) : (
    <TrendingDown className="h-4 w-4 text-red-600" />
  )

  return { text, isPositive, icon }
}

export function SnapshotComparison({ isOpen, onClose, comparison }: SnapshotComparisonProps) {
  const isDesktop = useMediaQuery('(min-width: 768px)')

  if (!comparison) return null

  const { snapshot1, snapshot2, delta, periodMismatch } = comparison

  const name1 = snapshot1.name || new Date(snapshot1.createdAt).toLocaleString('pt-BR')
  const name2 = snapshot2.name || new Date(snapshot2.createdAt).toLocaleString('pt-BR')

  const metrics = [
    {
      label: 'FRT Médio',
      previous: formatTime(snapshot1.frtAvgSeconds),
      current: formatTime(snapshot2.frtAvgSeconds),
      delta: formatDelta(delta.frt, true),
    },
    {
      label: 'TMR Médio',
      previous: formatTime(snapshot1.tmrAvgSeconds),
      current: formatTime(snapshot2.tmrAvgSeconds),
      delta: formatDelta(delta.tmr, true),
    },
    {
      label: 'Tickets',
      previous: snapshot1.totalTickets.toLocaleString(),
      current: snapshot2.totalTickets.toLocaleString(),
      delta: formatDelta(delta.tickets, false),
    },
    {
      label: 'Mensagens',
      previous: snapshot1.totalMessages.toLocaleString(),
      current: snapshot2.totalMessages.toLocaleString(),
      delta: formatDelta(delta.messages, false),
    },
    {
      label: 'Respostas',
      previous: snapshot1.totalResponses.toLocaleString(),
      current: snapshot2.totalResponses.toLocaleString(),
      delta: formatDelta(delta.responses, false),
    },
  ]

  const Content = (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto">
      {/* Header Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{name1}</CardTitle>
            <CardDescription>
              {new Date(snapshot1.startDate).toLocaleDateString('pt-BR')} até{' '}
              {new Date(snapshot1.endDate).toLocaleDateString('pt-BR')}
            </CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{name2}</CardTitle>
            <CardDescription>
              {new Date(snapshot2.startDate).toLocaleDateString('pt-BR')} até{' '}
              {new Date(snapshot2.endDate).toLocaleDateString('pt-BR')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* EC08: Period mismatch warning */}
      {periodMismatch && (
        <Alert variant="default" className="border-yellow-500">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
          <AlertDescription>
            <p className="font-semibold">Períodos diferentes</p>
            <p className="text-sm">
              Os períodos comparados têm durações diferentes. A comparação pode não ser proporcional.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Metrics Comparison Table */}
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Métrica</TableHead>
              <TableHead>Anterior</TableHead>
              <TableHead>Atual</TableHead>
              <TableHead>Delta</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {metrics.map((metric) => (
              <TableRow key={metric.label}>
                <TableCell className="font-medium">{metric.label}</TableCell>
                <TableCell className="text-muted-foreground">{metric.previous}</TableCell>
                <TableCell className="font-semibold">{metric.current}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {metric.delta.icon}
                    <Badge
                      variant="outline"
                      className={
                        metric.delta.isPositive
                          ? 'border-green-600 text-green-600'
                          : 'border-red-600 text-red-600'
                      }
                    >
                      {metric.delta.text}
                    </Badge>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Per Attendant Delta (optional, if data available) */}
      {delta.byAttendant.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Variação por Atendente</h3>
          <div className="space-y-2">
            {delta.byAttendant.slice(0, 5).map((attendant) => {
              const frtDelta = formatDelta(attendant.frtDelta, true)
              const tmrDelta = formatDelta(attendant.tmrDelta, true)
              const msgsDelta = formatDelta(attendant.messagesDelta, false)

              return (
                <Card key={attendant.userId}>
                  <CardContent className="p-3">
                    <p className="font-medium mb-2">{attendant.userName}</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="flex items-center gap-1">
                        FRT: {frtDelta.icon}
                        <span className={frtDelta.isPositive ? 'text-green-600' : 'text-red-600'}>
                          {frtDelta.text}
                        </span>
                      </span>
                      <span className="flex items-center gap-1">
                        TMR: {tmrDelta.icon}
                        <span className={tmrDelta.isPositive ? 'text-green-600' : 'text-red-600'}>
                          {tmrDelta.text}
                        </span>
                      </span>
                      <span className="flex items-center gap-1">
                        Msgs: {msgsDelta.icon}
                        <span className={msgsDelta.isPositive ? 'text-green-600' : 'text-red-600'}>
                          {msgsDelta.text}
                        </span>
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Comparação de Relatórios</DialogTitle>
          </DialogHeader>
          {Content}
        </DialogContent>
      </Dialog>
    )
  }

  // Mobile: Full page style
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] h-[90vh]">
        <DialogHeader>
          <DialogTitle>Comparação de Relatórios</DialogTitle>
        </DialogHeader>
        {Content}
      </DialogContent>
    </Dialog>
  )
}
